import type { DayId } from './days'
import { formatLocalIsoDate, getWeekRange } from './weekRange'
import type { TrainingBlock } from './protocolTypes'

export const WEEKDAY_IDS: DayId[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

/** Calendar date for a weekday in the week selected by weekOffset (Monday-start). */
export function dateForDayInWeek(dayNameId: string, weekOffset = 0): string {
  const { start } = getWeekRange(weekOffset)
  const idx = WEEKDAY_IDS.indexOf(dayNameId as DayId)
  const d = new Date(start)
  d.setHours(12, 0, 0, 0)
  if (idx < 0) return formatLocalIsoDate(d)
  d.setDate(d.getDate() + idx)
  return formatLocalIsoDate(d)
}

export function weekdayFromIsoDate(isoDate: string): DayId {
  const d = new Date(`${isoDate}T12:00:00`)
  const map: DayId[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  return map[d.getDay()]
}

export function isRestDayBlock(block: Pick<TrainingBlock, 'window_type' | 'exercises'> & { day_name_id?: string }): boolean {
  const wt = block.window_type?.toLowerCase() ?? ''
  if (wt === 'recovery' || wt === 'rest') return true
  if ((block.exercises?.length ?? 0) === 0 && wt !== 'training') return true
  return false
}

export function isTrainingBlock(block: Pick<TrainingBlock, 'window_type' | 'exercises'>): boolean {
  return !isRestDayBlock(block) && (block.exercises?.length ?? 0) > 0
}

export function normalizeTimeHHMM(value: string): string {
  const trimmed = value.trim()
  return trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed
}

export function formatWorkoutTimeDisplay(value: string | null | undefined, locale?: string): string {
  if (!value) return '—'
  const normalized = normalizeTimeHHMM(value)
  if (!normalized.includes(':')) return '—'
  return new Date(`1970-01-01T${normalized}:00`).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function workoutDurationMinutes(startTime: string, endTime: string): number {
  const [startH, startM] = normalizeTimeHHMM(startTime).split(':').map(Number)
  const [endH, endM] = normalizeTimeHHMM(endTime).split(':').map(Number)
  const startTotal = startH * 60 + startM
  let endTotal = endH * 60 + endM
  if (endTotal <= startTotal) endTotal += 24 * 60
  return Math.max(30, endTotal - startTotal)
}

export function workoutEndTimeFromStart(startTime: string, durationMinutes: number): string {
  const [h, m] = normalizeTimeHHMM(startTime).split(':').map(Number)
  const total = h * 60 + m + durationMinutes
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function endTimeForNewStart(startTime: string, endTime: string, newStartTime: string): string {
  const duration = workoutDurationMinutes(startTime, endTime)
  return workoutEndTimeFromStart(newStartTime, duration)
}

/** Upcoming training sessions aligned to real calendar weekdays (skips rest/recovery days). */
export function upcomingTrainingBlocks(training: TrainingBlock[], limit = 7): TrainingBlock[] {
  const today = new Date().toISOString().slice(0, 10)
  const out: TrainingBlock[] = []

  for (let offset = 0; offset < 21 && out.length < limit; offset++) {
    const d = new Date()
    d.setDate(d.getDate() + offset)
    const iso = d.toISOString().slice(0, 10)
    if (iso < today) continue

    const dayId = weekdayFromIsoDate(iso)
    const block = training.find((entry) => entry.day_name_id === dayId)
    if (block && isTrainingBlock(block)) {
      out.push({ ...block, scheduled_date: iso })
    }
  }

  return out
}
